import React, { useState, useEffect, useContext, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Search,
  Filter,
  Calendar,
  User,
  Banknote,
  ChevronDown,
  FileText,
  Download,
  Eye,
  Trash2,
  Plus
} from 'lucide-react';
import { WarehouseContext } from '../../Utility/UserContext';
import { getSalaryPayments, getUsers, deleteSalaryPayment } from '../../Utility/Api';
import { toast } from 'react-toastify';
import { cn } from '../../lib/utils';
import SalaryForm from './SalaryForm';
import { Deletemodel } from '../Common/Functions';

const SalaryList = () => {
  const { shopItem, subCompany, showLoader, hideLoader } = useContext(WarehouseContext);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [filters, setFilters] = useState({
    employee_id: '',
    type: '',
    from_date: '',
    to_date: ''
  });

  const [employees, setEmployees] = useState([]);

  const fetchPayments = useCallback(async () => {
    if (!shopItem || !subCompany) return;
    setLoading(true);
    try {
      const response = await getSalaryPayments({
        shop_id: subCompany.id,
        branch_id: shopItem.id,
        ...filters
      });
      if (response.data && response.data.status === 'success') {
        setPayments(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  }, [shopItem, subCompany, filters]);

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    showLoader();
    try {
      const response = await deleteSalaryPayment(deleteId);
      if (response.data.status === 'success') {
        toast.success("Payment reversed successfully");
        setShowDeleteModal(false);
        fetchPayments();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to reverse payment");
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    const fetchEmployees = async () => {
      if (shopItem?.id) {
        const response = await getUsers(1, "", shopItem.id);
        if (response.data && response.data.data) {
          setEmployees(response.data.data);
        }
      }
    };
    fetchEmployees();
  }, [shopItem]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const getStatusStyle = (type) => {
    switch (type) {
      case 'salary': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'bonus': return 'bg-green-100 text-green-700 border-green-200';
      case 'ot': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'advance': return 'bg-purple-100 text-purple-700 border-purple-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Salary Management</h1>
          <p className="text-sm text-slate-500">Track and manage employee payments for {shopItem?.shop_title}</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded font-bold hover:bg-blue-700 shadow-lg shadow-blue-500/20 transition-all active:scale-95"
        >
          <Plus className="w-5 h-5" /> New Payment
        </button>
      </div>

      {/* Filter Bar */}
      <div className="bg-white p-4 rounded border border-slate-200 shadow-sm flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[200px] space-y-1.5">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
            <User className="w-3 h-3" /> Employee
          </label>
          <select
            name="employee_id"
            value={filters.employee_id}
            onChange={handleFilterChange}
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all"
          >
            <option value="">All Employees</option>
            {employees.map(emp => (
              <option key={emp.user_Id} value={emp.user_Id}>{emp.full_name}</option>
            ))}
          </select>
        </div>

        <div className="w-40 space-y-1.5">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
            <Banknote className="w-3 h-3" /> Type
          </label>
          <select
            name="type"
            value={filters.type}
            onChange={handleFilterChange}
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All Types</option>
            <option value="salary">Salary</option>
            <option value="bonus">Bonus</option>
            <option value="ot">OT</option>
            <option value="advance">Advance</option>
          </select>
        </div>

        <div className="w-40 space-y-1.5">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
            <Calendar className="w-3 h-3" /> From
          </label>
          <input
            type="date"
            name="from_date"
            value={filters.from_date}
            onChange={handleFilterChange}
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <div className="w-40 space-y-1.5">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
            <Calendar className="w-3 h-3" /> To
          </label>
          <input
            type="date"
            name="to_date"
            value={filters.to_date}
            onChange={handleFilterChange}
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <button
          onClick={fetchPayments}
          className="p-2.5 bg-slate-100 text-slate-600 rounded hover:bg-slate-200 transition-colors"
          title="Refresh List"
        >
          <Search className="w-5 h-5" />
        </button>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Employee</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Amount (INR)</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Mode</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-slate-400 italic">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      Loading payments...
                    </div>
                  </td>
                </tr>
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-slate-400 italic">
                    No payment records found for the selected filters.
                  </td>
                </tr>
              ) : (
                payments.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 text-xs font-bold">
                          {p.employee_name?.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-800">{p.employee_name}</p>
                          <p className="text-[10px] text-slate-400 uppercase font-medium">{p.branch_title}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={cn(
                        "px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border",
                        getStatusStyle(p.type)
                      )}>
                        {p.type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-slate-600">{p.payment_date}</p>
                      {p.from_date && (
                        <p className="text-[10px] text-slate-400 italic">{p.from_date} to {p.to_date}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {p.deductions && p.deductions.length > 0 ? (() => {
                        const ded = parseFloat(p.deductions[0].amount || 0);
                        const gross = parseFloat(p.amount || 0) - ded;
                        const net = gross - ded;
                        return (
                          <div className="space-y-0.5">
                            <p className="text-[10px] text-slate-400 font-medium">Gross: ₹{gross.toFixed(2)}</p>
                            <p className="text-[10px] text-orange-500 font-medium">Ded: ₹{ded.toFixed(2)}</p>
                            <p className="text-sm font-black text-blue-700">Net: ₹{net.toFixed(2)}</p>
                          </div>
                        );
                      })() : (
                        <p className="text-sm font-black text-slate-800">₹{p.amount}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-xs text-slate-500 capitalize">{p.payment_mode.replace('_', ' ')}</p>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2 transition-opacity">
                        <button
                          onClick={() => handleDeleteClick(p.id)}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Reverse Payment"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Footer Section */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Total Disbursed</p>
          <p className="text-xl font-black text-slate-800">₹{
            payments.reduce((acc, p) => {
              const gross = parseFloat(p.amount || 0);
              // For salary with advance deductions, use the net (gross - deduction)
              const ded = (p.type === 'salary' && p.deductions && p.deductions.length > 0)
                ? parseFloat(p.deductions[0].amount || 0)
                : 0;
              return acc + (gross - ded);
            }, 0).toFixed(2)
          }</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <p className="text-[10px] font-bold text-blue-400 uppercase tracking-wider mb-1">Total Salaries</p>
          <p className="text-xl font-black text-slate-800">₹{
            payments
              .filter(p => p.type === 'salary')
              .reduce((acc, p) => {
                const ded = (p.deductions && p.deductions.length > 0)
                  ? parseFloat(p.deductions[0].amount || 0)
                  : 0;
                // Subtract deduction once: p.amount - ded (net salary paid)
                return acc + (parseFloat(p.amount || 0) - ded);
              }, 0).toFixed(2)
          }</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <p className="text-[10px] font-bold text-green-400 uppercase tracking-wider mb-1">Total Bonuses</p>
          <p className="text-xl font-black text-slate-800">₹{payments.filter(p => p.type === 'bonus').reduce((acc, p) => acc + parseFloat(p.amount), 0).toFixed(2)}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <p className="text-[10px] font-bold text-purple-400 uppercase tracking-wider mb-1">Total Advances</p>
          <p className="text-xl font-black text-slate-800">₹{
            (() => {
              // Total advance payments given out
              const totalGiven = payments
                .filter(p => p.type === 'advance')
                .reduce((acc, p) => acc + parseFloat(p.amount || 0), 0);
              // Total advance amounts recovered via salary deductions
              const totalRecovered = payments
                .filter(p => p.type === 'salary' && p.deductions && p.deductions.length > 0)
                .reduce((acc, p) => acc + parseFloat(p.deductions[0].amount || 0), 0);
              // Net outstanding advance = given - recovered
              return Math.max(0, totalGiven - totalRecovered).toFixed(2);
            })()
          }</p>
        </div>
      </div>

      {/* Salary Form Modal */}
      {showForm && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm animate-in fade-in duration-300 flex items-center justify-center p-3 sm:p-4">
          <div className="animate-in zoom-in-95 duration-300 w-full max-w-4xl flex items-center justify-center">
            <SalaryForm
              onClose={() => setShowForm(false)}
              onSuccess={fetchPayments}
            />
          </div>
        </div>,
        document.body
      )}

      {showDeleteModal && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm animate-in fade-in duration-300 flex items-center justify-center p-3 sm:p-4">
          <Deletemodel
            setShowModal={setShowDeleteModal}
            handleDelete={confirmDelete}
            text="Are you sure you want to reverse this payment? This will update the employee balance accordingly."
          />
        </div>,
        document.body
      )}
    </div>
  );
};

export default SalaryList;
